import { useMemo } from "react"

import {
  Container,
  Heading,
  Icon,
  InViewContainer,
  MobileStickyCta,
} from "@trueskin-web/components"
import { formatCurrency, isFloat } from "@trueskin-web/functions"
import { useIsScreenSizeSmall } from "@trueskin-web/theme"
import { arrowLongRightIcon } from "@trueskin-web/theme/icons/arrowLongRightIcon"
import i18next, { Trans, useTranslation } from "@trueskin-web/translations"

import SubmitButton from "../../components/submit-button"
import withPresetsActions from "../../components/with-presets-actions"
import withPresetsData from "../../components/with-presets-data"

import Plan from "./plan"

const mapCurrentPresetsToPlans = (presets) => {
  if (!presets.length) {
    return []
  }

  const variants = presets.map((currentPreset) => {
    const isSubscription = currentPreset.occurrence !== "occ_once"
    const locale = currentPreset.summary.locale
    const instalments = currentPreset.summary.instalments.count
    const { total, totalWithoutDiscounts, currency } =
      currentPreset.summary.values.perInstalment

    const title = isSubscription
      ? i18next.t("TwoMonthsPresets.title", {
          count: instalments,
        })
      : currentPreset.details.name

    const priceTitle = formatCurrency(
      total / 100,
      currency,
      isFloat(total / 100) ? 2 : 0
    )

    const priceWithoutDiscounts = formatCurrency(
      totalWithoutDiscounts / 100,
      currency
    )

    const benefits = i18next
      .t(`TwoMonthsPresets.benefits.${instalments}`)
      .map((lines) =>
        i18next.t(lines, {
          total: currentPreset.summary.values.total / 100,
        })
      )

    return {
      isSubscription,
      title,
      priceTitle,
      priceWithoutDiscounts,
      locale,
      instalments,
      subscriptionBenefits: isSubscription ? benefits : [],
      id: currentPreset.id,
      isSelected: currentPreset.isSelected,
      isRecommended: currentPreset.isRecommended,
      description: currentPreset.details.description,
      image: currentPreset.details.image,
      details: currentPreset.details.plan,
    }
  })

  return variants.reduce(
    (accum, current) => {
      if (current.isSubscription) {
        accum[0].variants.push(current)
        return accum
      }

      return [...accum, { group: false, variants: [current] }]
    },
    [{ group: true, variants: [] }]
  )
}

const Plans = ({
  presetList,
  selectedPlanId,
  isSubmitting,
  onSelect,
  onSubmit,
}) => {
  const { t } = useTranslation()
  const isMobile = useIsScreenSizeSmall()

  const plans = mapCurrentPresetsToPlans(presetList)

  const selectedPlan = useMemo(() => {
    return plans
      .flatMap((plan) => plan.variants)
      .find((variant) => variant.id === selectedPlanId)
  }, [plans, selectedPlanId])

  return (
    <InViewContainer sx={{ mx: ["-16px", "auto"] }}>
      {({ isInView }) => (
        <>
          <Container sx={{ backgroundColor: "lightBlue", py: 5, px: [4, 5] }}>
            <Heading
              sx={{
                mb: [4, 6],
                textAlign: "center",
                lineHeight: "body",
              }}
            >
              <Trans i18nKey="ChoosePlan.Plans.title" />
            </Heading>

            <div
              sx={{
                display: [null, "grid"],
                gridTemplateColumns: [null, "repeat(3, 33%)"],
                justifyContent: "center",
                gap: [null, 4],
              }}
            >
              {plans.map((plan, index) => (
                <Plan
                  key={index}
                  plan={plan}
                  selectedPlanId={selectedPlanId}
                  onSelect={onSelect}
                  onSubmit={onSubmit}
                  sx={{
                    order: [
                      null,
                      plan.group ? Math.ceil(plans.length / 2) : index,
                    ],
                  }}
                />
              ))}
            </div>
          </Container>
          {isMobile && selectedPlan && (
            <SubmitButton
              as={MobileStickyCta}
              dataTestId="web-portal-plan-selection-mobile-cta"
              show={isInView}
              isLoading={isSubmitting}
              withArrow={false}
              title={
                <>
                  {t("ChoosePlan.continueButton", {
                    title: selectedPlan.isSubscription
                      ? "Auto-Refill"
                      : selectedPlan.title,
                  })}
                  <Icon icon={arrowLongRightIcon} sx={{ mt: "2px", ml: 1 }} />
                </>
              }
              onClick={() => onSubmit(selectedPlanId)}
            />
          )}
        </>
      )}
    </InViewContainer>
  )
}

export default withPresetsData(withPresetsActions(Plans))
